import type { AxiosResponse } from 'axios';

import { useCurrentUserPermissions } from '@/generated/hooks/agora/v1/rbac';
import type { V1CurrentUserPermissionsResponse } from '@/generated/hooks/agora/models';

import { normalizePlaceholders } from './match';

/**
 * Builds an O(1)-lookup Set keyed by `${HTTP_METHOD} ${resource_path}`.
 *
 * Placeholder names in `resource_path` (e.g. `{automation_id}`) are collapsed
 * to `{*}` so they match the frontend's normalized lookup key, which uses
 * shorter aliases from the OpenAPI spec (e.g. `{automation}`).
 */
export function buildPermissionsIndex(
  response?: V1CurrentUserPermissionsResponse,
): Set<string> {
  const index = new Set<string>();
  for (const p of response?.permissions ?? []) {
    // Skip grants that are missing resource_action or resource_path
    if (!p.resource_action || !p.resource_path) continue;

    // Normalize method casing, strip trailing slashes, and canonicalize
    // any remaining `{name}` placeholders to `{*}`.
    const method = p.resource_action.toUpperCase();
    const trimmed = p.resource_path.replace(/\/+$/, '') || p.resource_path;
    const path = normalizePlaceholders(trimmed);
    index.add(`${method} ${path}`);
  }
  return index;
}

// Module-scoped so React Query's `select` memoizes against a stable identity
// instead of rebuilding the index on every render.
function selectPermissionsIndex(
  res: AxiosResponse<V1CurrentUserPermissionsResponse>,
): Set<string> {
  return buildPermissionsIndex(res.data);
}

/**
 * Wraps useCurrentUserPermissions with an indexed Set as the cached value.
 * `data` is `Set<string> | undefined`; check membership with
 * `data?.has(\`${method} ${path}\`)`.
 */
export function usePermissionsIndex({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useCurrentUserPermissions<Set<string>>(undefined, {
    query: {
      enabled,
      retry: false,
      staleTime: 5 * 60 * 1000,
      select: selectPermissionsIndex,
    },
  });
}
